"use client";

import { useEffect, useMemo, useRef } from "react";

interface ParsedCount {
  target: number;
  decimals: number;
  prefix: string;
  suffix: string;
}

/**
 * Parses a CMS count string into its numeric portion and surrounding text.
 * Returns `null` when there is no leading numeric segment to animate.
 *
 * Examples:
 *   "50"     → { target: 50,  decimals: 0, prefix: "",  suffix: ""    }
 *   "500k+"  → { target: 500, decimals: 0, prefix: "",  suffix: "k+"  }
 *   "$1.5M"  → { target: 1.5, decimals: 1, prefix: "$", suffix: "M"   }
 *   "soon"   → null
 */
function parseCount(value: string): ParsedCount | null {
  if (!value) return null;
  const match = value.match(/^(\D*?)(-?\d+(?:\.\d+)?)(.*)$/);
  if (!match) return null;
  const numericText = match[2];
  const target = Number(numericText);
  if (!Number.isFinite(target)) return null;
  const dotIdx = numericText.indexOf(".");
  const decimals = dotIdx === -1 ? 0 : numericText.length - dotIdx - 1;
  return {
    target,
    decimals,
    prefix: match[1] ?? "",
    suffix: match[3] ?? "",
  };
}

const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4);

interface CountUpNumberProps {
  /** Raw count string from the CMS — e.g. "50", "500k+", "1.5M", "N/A". */
  value: string;
  /** Animation duration in ms. Defaults to 1500. */
  durationMs?: number;
  /** Class for the outer wrapper. */
  className?: string;
  /** Class for the inner span containing the animated digits. */
  numberClassName?: string;
}

/**
 * Renders a count string and animates the numeric portion from 0 up to the
 * parsed value when the element scrolls into view. Falls back to rendering
 * the raw value (no animation) when no numeric portion is present, and skips
 * animation entirely when the user prefers reduced motion.
 *
 * Performance notes:
 *   - DOM is mutated via `ref.textContent` per rAF tick, never via React
 *     state, so the surrounding tree never re-renders during animation.
 *   - One `IntersectionObserver` per instance, disconnected after the first
 *     intersection so it never lingers in memory.
 *   - SSR / pre-hydration markup shows the final formatted value, which is
 *     what users (and crawlers) should see when JS is unavailable.
 */
export default function CountUpNumber({
  value,
  durationMs = 1500,
  className,
  numberClassName,
}: CountUpNumberProps) {
  const numberRef = useRef<HTMLSpanElement>(null);
  const parsed = useMemo(() => parseCount(value), [value]);

  useEffect(() => {
    if (!parsed) return;
    const el = numberRef.current;
    if (!el) return;

    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      el.textContent = parsed.target.toFixed(parsed.decimals);
      return;
    }

    let rafId: number | null = null;
    let observer: IntersectionObserver | null = null;
    let hasRun = false;

    const animate = () => {
      if (hasRun) return;
      hasRun = true;
      const startTs = performance.now();
      el.textContent = (0).toFixed(parsed.decimals);
      const tick = (now: number) => {
        const t = Math.min(1, (now - startTs) / durationMs);
        const eased = easeOutQuart(t);
        el.textContent = (parsed.target * eased).toFixed(parsed.decimals);
        if (t < 1) {
          rafId = requestAnimationFrame(tick);
        } else {
          rafId = null;
        }
      };
      rafId = requestAnimationFrame(tick);
    };

    if (typeof IntersectionObserver === "undefined") {
      animate();
    } else {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            animate();
            observer?.disconnect();
            observer = null;
          }
        },
        { threshold: 0.25 },
      );
      observer.observe(el);
    }

    return () => {
      observer?.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [parsed, durationMs]);

  if (!parsed) {
    return <span className={className}>{value}</span>;
  }

  return (
    <span className={className}>
      {parsed.prefix}
      <span ref={numberRef} className={numberClassName}>
        {parsed.target.toFixed(parsed.decimals)}
      </span>
      {parsed.suffix}
    </span>
  );
}
